/**
 * Search Utilities - Alphabetical matching logic for InfoCard highlights
 *
 * Core utility: No internal dependencies.
 *
 * Matching priority:
 * 1. Exact match (case-insensitive)
 * 2. Starts with search term
 * 3. Contains search term, preferring:
 *    a) Fewer characters before the match
 *    b) Alphabetically first if equal prefix length
 */

export interface SearchMatch {
  /** The field name that matched */
  field: string;
  /** The value that matched (displayed to user) */
  value: string;
  /** Match quality score (lower is better) */
  score: number;
}

/**
 * Calculate match score for a value against a search term.
 * Lower score = better match.
 * Returns null if no match.
 *
 * Scoring:
 * - Exact match: 0
 * - Starts with: 1000 + alphabetical position
 * - Contains: 2000 + (chars before match * 100) + alphabetical position
 */
export function calculateMatchScore(
  value: string,
  searchTerm: string
): number | null {
  if (!value || !searchTerm) return null;

  const valueLower = value.toLowerCase();
  const searchLower = searchTerm.toLowerCase().trim();

  if (!searchLower) return null;

  // Exact match - best score
  if (valueLower === searchLower) {
    return 0;
  }

  // Starts with - second best
  if (valueLower.startsWith(searchLower)) {
    // Add small alphabetical tiebreaker (charCode of first char)
    const alphabeticalBonus = valueLower.charCodeAt(0);
    return 1000 + alphabeticalBonus;
  }

  // Contains - third best
  const containsIndex = valueLower.indexOf(searchLower);
  if (containsIndex > 0) {
    // Score based on: chars before match + alphabetical tiebreaker
    const charsBefore = containsIndex;
    const alphabeticalBonus = valueLower.charCodeAt(0);
    return 2000 + (charsBefore * 100) + alphabeticalBonus;
  }

  // No match
  return null;
}

/**
 * Find the best matching field from a record of searchable fields.
 * Returns the field name and value with the best score.
 */
export function findBestMatch(
  fields: Record<string, string | number | null | undefined>,
  searchTerm: string,
  excludeFields: string[] = []
): SearchMatch | null {
  let bestMatch: SearchMatch | null = null;

  for (const [field, rawValue] of Object.entries(fields)) {
    // Skip excluded fields (already matched by previous search term)
    if (excludeFields.includes(field)) continue;

    // Convert value to string
    const value = rawValue != null ? String(rawValue) : '';
    if (!value) continue;

    const score = calculateMatchScore(value, searchTerm);
    if (score === null) continue;

    // Check if this is better than current best
    if (bestMatch === null || score < bestMatch.score) {
      bestMatch = { field, value, score };
    }
  }

  return bestMatch;
}

/**
 * Parse search query into terms (semicolon-separated).
 * Returns up to 2 non-empty terms.
 */
export function parseSearchTerms(query: string): string[] {
  if (!query) return [];

  return query
    .split(';')
    .map(term => term.trim())
    .filter(term => term.length > 0)
    .slice(0, 2); // Max 2 terms
}

/**
 * Find highlights for a card based on search terms.
 * Returns up to 2 highlights (one per search term).
 * Returns null if ANY search term doesn't match (AND logic - must match ALL terms).
 */
export function findSearchHighlights(
  fields: Record<string, string | number | null | undefined>,
  searchQuery: string
): string[] | null {
  const terms = parseSearchTerms(searchQuery);

  if (terms.length === 0) {
    // No search query - show all cards, no highlights
    return [];
  }

  const highlights: string[] = [];
  const usedFields: string[] = [];

  for (const term of terms) {
    const match = findBestMatch(fields, term, usedFields);

    if (match) {
      highlights.push(match.value);
      usedFields.push(match.field);
    } else {
      // AND logic: if ANY term doesn't match, filter out the card
      return null;
    }
  }

  return highlights;
}

/**
 * Check if a card matches the search query.
 * Returns true if ALL search terms have matches (AND logic).
 */
export function cardMatchesSearch(
  fields: Record<string, string | number | null | undefined>,
  searchQuery: string
): boolean {
  return findSearchHighlights(fields, searchQuery) !== null;
}
